import React, { useState } from "react";
import {
  Menu,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Send,
  BadgeCheck,
  FolderOpen,
  PenTool,
  ShieldCheck,
  KeyRound,
  Settings,
  FileCheck2,
  Building2,
  BarChart2,
  BrainCircuit,
  ServerCog,
  ScrollText,
  Link2,
  Users,
  Plug,
  TrendingUp,
  Clock
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import UserMenu from "./UserMenu";

const cssStyles = `
  .sidebar-aside {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  background:#071F4D;
  box-shadow:4px 0 20px rgba(0,0,0,.25);
  box-shadow: 4px 0 24px rgba(102, 16, 242, 0.18);
  transition: width 280ms cubic-bezier(0.4, 0, 0.2, 1);

  display: flex;
  flex-direction: column;

  z-index: 100;

  overflow: hidden; /* Changed from overflow-y: auto */
}
  .sidebar-w-collapsed { width: 72px; }
  .sidebar-w-expanded { width: 200px; }

  .sidebar-top-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 14px 10px 14px;
    min-height: 64px;
  }
  .sidebar-logo {
    height: 64px;
    width: auto;
    mix-blend-mode: multiply;
  }
  .sidebar-toggle-btn {
    color: rgba(255,255,255,0.8);
    background: rgba(255,255,255,0.1);
    border: none;
    border-radius: 8px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 150ms ease;
  }
  .sidebar-toggle-btn:hover { background: rgba(255,255,255,0.2); color: #fff; }

  .sidebar-divider {
    height: 1px;
    background: rgba(255,255,255,0.12);
    margin: 4px 14px 8px 14px;
  }

  .sidebar-nav{
    flex:1;
    overflow-y:auto;
    overflow-x:hidden;
    padding:12px 10px 20px;
    scrollbar-width:none;
}

.sidebar-nav::-webkit-scrollbar {
  display: none;
}

  .sidebar-nav-btn {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 8px;
    height: 44px;
    border-radius: 10px;
    font-size: 13.5px;
    font-weight: 500;
    color: rgba(255,255,255,0.75);
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background 150ms ease, color 150ms ease;
    white-space: nowrap;
    margin-bottom: 2px;
  }
  .sidebar-nav-btn:hover {
    background: rgba(255,255,255,0.12);
    color: #fff;
  }
  .sidebar-nav-btn.active {
    background: #ffffff;
    color: #5b21b6;
    font-weight: 600;
  }
  .sidebar-nav-btn.active-pink {
    background: #fce7f3;
    color: #be185d;
    font-weight: 600;
  }

  .sidebar-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }

  .sidebar-submenu-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    height: 44px;
    border-radius: 10px;
    font-size: 13.5px;
    font-weight: 500;
    color: rgba(255,255,255,0.75);
    background: none;
    border: none;
    cursor: pointer;
    transition: background 150ms ease, color 150ms ease;
    white-space: nowrap;
    margin-bottom: 2px;
  }
  .sidebar-submenu-toggle:hover {
    background: rgba(255,255,255,0.12);
    color: #fff;
  }
  .sidebar-submenu-toggle.active {
    background: #ffffff;
    color: #5b21b6;
    font-weight: 600;
  }
  .sidebar-flex-gap {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .sidebar-submenu-panel {
    margin-left: 42px;
    margin-bottom: 4px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .sidebar-submenu-heading {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(255,255,255,0.4);
    font-size: 10px;
    font-weight: 600;
    margin: 8px 0 4px 0;
    padding-left: 8px;
  }

  .sidebar-section-heading {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(255,255,255,0.35);
    font-size: 10.5px;
    font-weight: 700;
    margin: 16px 0 6px 0;
    padding-left: 10px;
  }
  .sidebar-section-heading:first-child {
    margin-top: 2px;
  }
  .sidebar-submenu-item {
    cursor: pointer;
    font-size: 13px;
    color: rgba(255,255,255,0.65);
    padding: 6px 8px;
    border-radius: 7px;
    transition: background 150ms ease, color 150ms ease;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .sidebar-submenu-item:hover {
    background: rgba(255,255,255,0.1);
    color: #ffffff;
  }
  .sidebar-submenu-item.active {
    background: rgba(255,255,255,0.16);
    color: #ffffff;
    font-weight: 600;
  }

  .sidebar-footer {
    padding: 12px 10px;
    border-top: 1px solid rgba(255,255,255,0.1);
    flex-shrink: 0;
  }
`;

const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [settingsOpen, setSettingsOpen] = useState(false);

  // Items grouped under "Command Center"
  const commandCenterItems = [
    { icon: ClipboardCheck, label: "Dashboard", route: "/dashboard" },
    { icon: BrainCircuit, label: "Security Analytics", route: "/AI-security" },
  ];

  // Items grouped under "Trust Operations"
  const trustOperationsItems = [
    { icon: FileText, label: "Sign Documents", route: "/app" },
    { icon: PenTool, label: "Sign Artifacts", route: "/choose-signing-method" },
    {
      icon: BadgeCheck,
      label: "Verify & Validate",
      route: "/verify-trust",
      // Keep this item highlighted while the user is inside the verify wizard
      // (document and artifact flows).
      matches: [
        "/verification",
        "/artifact-verification",
        "/automatic-verification",
        "/artifact-integrity",
        "/ai-tamper-detection",
        "/policy-key-validation",
        "/verification-result",
        "/verification-report",
      ],
    },
    { icon: Send, label: "Request Signatures", route: "/request-signatures" },
    { icon: Send, label: "Pending Request", route: "/request-status" },
  ];

  // Items grouped under "Digital Identity"
  const digitalIdentityItems = [
    {icon: KeyRound, label: "Digital Identity", route: "/signdoc" },
    { icon: FileCheck2, label: "Certificate", route: "/settings/certificates" },
    { icon: ServerCog, label: "HSM & Key", route: "/HSM-management" },
    { icon: ShieldCheck, label: "Trust Policies", route: "/trust-policies" },
  ];

  // Items grouped under "Trust & Evidence"
  const trustandEvidenceItems = [
    { icon: ScrollText, label: "Audit Trails", route: "/settings/audit-logs" },
    { icon: FolderOpen, label: "Secure Vault", route: "/drive" },
    { icon: Link2, label: "Blockchain / IPFS", route: "/settings/blockchain-anchoring" },
    { icon: ShieldCheck, label: "Compliance", route: "/Compliance" },
    { icon: Clock, label: "TimeStamping", route: "/timestamps" },
    { icon: TrendingUp, label: "Trust Score", route: "/trust-score" },
  ];

  // Items grouped under "Administration"
  const administrationItems = [
    { icon: Users, label: "Users & Roles", route: "/users-control" },
    { icon: Building2, label: "Organizations", route: "/settings/organizations" },
    { icon: Plug, label: "Integrations", route: "/settings/integrations" },
    { icon: BarChart2, label: "Reports", route: "/reports" },
  ];

  const settingsSubItems = {
    "System Settings": [
      { label: "My profile", route: "/settings/settings-profile" },
      { label: "Security", route: "/settings/security-authentication" },
      { label: "Backup and Recovery", route: "/settings/backup-recovery" },
      { label: "System Preferences", route: "/settings/system-preferences" },
      { label: "My notifications", route: "/settings/my-notifications" },
    ],
  };

  return (
    <>
      <style>{cssStyles}</style>

      <aside
        className={`sidebar-aside ${isCollapsed ? "sidebar-w-collapsed" : "sidebar-w-expanded"}`}
      >
        {/* Top: Logo & Toggle */}
          <div
  style={{
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  }}
>
  <div
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: isCollapsed ? "center" : "space-between",
    padding: "12px 8px 18px",
  }}
>
  {!isCollapsed && (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginLeft: "6px",
    }}
  >
    <ShieldCheck
      size={24}
      color="#ffffff"
      strokeWidth={2.2}
    />

    <span
      style={{
        color: "#ffffff",
        fontSize: "22px",
        fontWeight: "700",
        letterSpacing: "-0.3px",
      }}
    >
      I-PQSign
    </span>

    <span
      style={{
        color: "#8B5CF6",
        fontSize: "10px",
        fontWeight: "600",
        alignSelf: "flex-start",
        marginTop: "2px",
      }}
    >
      ™
    </span>
  </div>
)}

  <button
    onClick={() => setIsCollapsed(!isCollapsed)}
    className="sidebar-toggle-btn"
  >
    <Menu size={18} />
  </button>
</div>

          {/* Nav Items */}
          <nav className="sidebar-nav">
            {/* Command Center section (Dashboard + AI Security) - flat items, no dropdown */}
            {!isCollapsed && (
              <p className="sidebar-section-heading">Command Center</p>
            )}
            {commandCenterItems.map(({ icon: Icon, label, route }) => {
              const isActive = location.pathname === route;
              return (
                <button
                  key={label}
                  onClick={() => navigate(route)}
                  className={`sidebar-nav-btn ${isActive ? "active" : ""}`}
                >
                  <span className="sidebar-icon-wrapper">
                    <Icon
                      size={18}
                      color={isActive ? "#5B21B6" : "rgba(255,255,255,0.85)"}
                    />
                  </span>
                  {!isCollapsed && <span>{label}</span>}
                </button>
              );
            })}

            {/* Trust Operations section - flat items, no dropdown */}
            {!isCollapsed && (
              <p className="sidebar-section-heading">Trust Operations</p>
            )}
            {trustOperationsItems.map(({ icon: Icon, label, route, matches }) => {
              const isActive =
                location.pathname === route || (matches || []).includes(location.pathname);
              const isPink = label === "Request Signatures";
              const isActivePink = isActive && isPink;
              const isActiveBlue = isActive && !isPink;

              let activeClass = "";
              let iconColor = "rgba(255,255,255,0.85)";
              if (isActivePink) {
                activeClass = "active-pink";
                iconColor = "#be185d";
              } else if (isActiveBlue) {
                activeClass = "active";
                iconColor = "#5B21B6";
              }

              return (
                <button
                  key={label}
                  onClick={() => navigate(route)}
                  className={`sidebar-nav-btn ${activeClass}`}
                >
                  <span className="sidebar-icon-wrapper">
                    <Icon
                      size={18}
                      color={iconColor}
                    />
                  </span>
                  {!isCollapsed && <span>{label}</span>}
                </button>
              );
            })}

            {/* Digital Identity section - flat items, no dropdown */}
            {!isCollapsed && (
              <p className="sidebar-section-heading">Digital Identity</p>
            )}
            {digitalIdentityItems.map(({ icon: Icon, label, route }) => {
              const isActive = location.pathname === route;
              return (
                <button
                  key={label}
                  onClick={() => navigate(route)}
                  className={`sidebar-nav-btn ${isActive ? "active" : ""}`}
                >
                  <span className="sidebar-icon-wrapper">
                    <Icon
                      size={18}
                      color={isActive ? "#5B21B6" : "rgba(255,255,255,0.85)"}
                    />
                  </span>
                  {!isCollapsed && <span>{label}</span>}
                </button>
              );
            })}

            {/* Trust & Evidence section - flat items, no dropdown */}
            {!isCollapsed && (
              <p className="sidebar-section-heading">Trust & Evidence</p>
            )}
            {trustandEvidenceItems.map(({ icon: Icon, label, route }) => {
              const isActive = location.pathname === route;
              return (
                <button
                  key={label}
                  onClick={() => navigate(route)}
                  className={`sidebar-nav-btn ${isActive ? "active" : ""}`}
                >
                  <span className="sidebar-icon-wrapper">
                    <Icon
                      size={18}
                      color={isActive ? "#5B21B6" : "rgba(255,255,255,0.85)"}
                    />
                  </span>
                  {!isCollapsed && <span>{label}</span>}
                </button>
              );
            })}

            {/* Administration section - flat items, no dropdown */}
            {!isCollapsed && (
              <p className="sidebar-section-heading">Administration</p>
            )}
            {administrationItems.map(({ icon: Icon, label, route }) => {
              const isActive = location.pathname === route;
              return (
                <button
                  key={label}
                  onClick={() => navigate(route)}
                  className={`sidebar-nav-btn ${isActive ? "active" : ""}`}
                >
                  <span className="sidebar-icon-wrapper">
                    <Icon
                      size={18}
                      color={isActive ? "#5B21B6" : "rgba(255,255,255,0.85)"}
                    />
                  </span>
                  {!isCollapsed && <span>{label}</span>}
                </button>
              );
            })}

            {/* Settings with Submenu */}
            <div>
              <button
                onClick={() => setSettingsOpen(!settingsOpen)}
                className="sidebar-submenu-toggle"
              >
                <div className="sidebar-flex-gap">
                  <span className="sidebar-icon-wrapper">
                    <Settings size={18} color="rgba(255,255,255,0.85)" />
                  </span>
                  {!isCollapsed && <span>Settings</span>}
                </div>
                {!isCollapsed &&
                  (settingsOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />)}
              </button>

              {settingsOpen && !isCollapsed && (
                <div className="sidebar-submenu-panel">
                  {Object.entries(settingsSubItems).map(([groupLabel, items]) => (
                    <React.Fragment key={groupLabel}>
                      <p className="sidebar-submenu-heading">{groupLabel}</p>
                      {items.map((item) => (
                        <div
                          key={item.label}
                          onClick={() => navigate(item.route)}
                          className="sidebar-submenu-item"
                        >
                          {item.label}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </nav>
        </div>

        {/* Profile - UserMenu at bottom */}
        <div className="sidebar-footer">
          <UserMenu collapsed={isCollapsed} />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;